import { Component, DestroyRef, ElementRef, HostListener, ViewChild, computed, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs/operators';
import { LiveLesson, LiveLessonTag } from 'src/app/types/live-lesson-types';
import { SearchBarComponent } from 'src/app/components/ui/search-bar/search-bar.component';
import { LiveLessonScheduleItemComponent } from './live-lesson-schedule-item/live-lesson-schedule-item.component';

@Component({
  selector: 'app-live-lesson-schedule',
  standalone: true,
  imports: [CommonModule, SearchBarComponent, LiveLessonScheduleItemComponent],
  templateUrl: './live-lesson-schedule.component.html',
  styleUrl: './live-lesson-schedule.component.scss',
})
export class LiveLessonScheduleComponent {
  // Scoped to just the filter dropdown, not the whole component — the backdrop and the rest of
  // the drawer are also part of this component's own template, so checking against the host
  // element meant almost nothing ever counted as "outside" and the panel never closed.
  @ViewChild('filterDropdown') private filterDropdownRef?: ElementRef<HTMLElement>;

  private router = inject(Router);
  private destroyRef = inject(DestroyRef);

  isOpen = input<boolean>(false);
  lessons = input<LiveLesson[]>([]);
  tags = input<LiveLessonTag[]>([]);
  closed = output<void>();

  constructor() {
    // Clicking a lesson card inside the drawer (schedule-item, or the detail route it links to)
    // navigates the whole app, not just content behind the drawer — closing once that
    // navigation actually lands means the newly-loaded page isn't left sitting under an open
    // drawer the user has to dismiss separately.
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => {
        if (this.isOpen()) {
          this.closed.emit();
        }
      });
  }

  searchKeys = ['title', 'searchableTagText'];
  filteredLessons = signal<LiveLesson[]>([]);
  selectedTagIds = signal<Set<number>>(new Set());
  filterOpen = signal(false);

  selectedTags = computed(() =>
    this.tags().filter((t) => this.selectedTagIds().has(t.id)),
  );

  displayedLessons = computed(() => {
    const ids = this.selectedTagIds();
    if (ids.size === 0) return this.filteredLessons();
    return this.filteredLessons().filter((l) => l.tags.some((t) => ids.has(t.id)));
  });

  // Same outside-click-closes pattern as ui/dropdown's DropdownComponent — this panel is
  // hand-rolled rather than reusing that component because it needs multi-select checkboxes,
  // not DropdownComponent's single-value ControlValueAccessor model. Only closes the dropdown —
  // selectedTagIds is untouched, so any selections made stay applied.
  @HostListener('document:click', ['$event'])
  handleOutsideClick(event: MouseEvent): void {
    const dropdownEl = this.filterDropdownRef?.nativeElement;
    if (this.filterOpen() && dropdownEl && !dropdownEl.contains(event.target as Node)) {
      this.filterOpen.set(false);
    }
  }

  toggleFilterPanel(): void {
    this.filterOpen.set(!this.filterOpen());
  }

  toggleTag(tagId: number): void {
    const next = new Set(this.selectedTagIds());
    if (next.has(tagId)) {
      next.delete(tagId);
    } else {
      next.add(tagId);
    }
    this.selectedTagIds.set(next);
  }

  clearTags(): void {
    this.selectedTagIds.set(new Set());
  }
}
